import { useCallback, useContext, useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';

import { User } from '../types/types';
import { AppRoute, ButtonType, InputType, SnackbarType, UserRole } from '../types/enums';
import { NotificationContext, UserContext } from '../App';
import { Button, DeletePopup, Input } from '../components';
import Frame from '../layout/Frame';
import { fetchUser, updateUser } from '../requests/requests';
import useErrorHandler from '../hooks/useErrorHandler';
import Panel from '../layout/Panel';
import DeactivatePopup from '../components/DeactivatePopup/DeactivatePopup';

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const errorHandler = useErrorHandler();
  const { user } = useContext(UserContext);
  const notify = useContext(NotificationContext);
  const [selectedUser, setSelectedUser] = useState<User>({} as User);
  const [showActivityPopup, setShowActivityPopup] = useState(false);
  const [deltas, setDeltas] = useState({
    dashboard: 0,
    website: 0
  });

  const refreshUser = useCallback(() => {
    fetchUser(id as string)
      .then((res) => {
        setSelectedUser(res.data.user);
        setDeltas({
          dashboard: res.data.user.subscription.delta_dashboard_count ?? 0,
          website: res.data.user.subscription.delta_website_count ?? 0
        });
      })
      .catch(errorHandler);
  }, []);

  useEffect(() => {
    if (user?.role !== UserRole.ADMIN) {
      navigate(AppRoute.DASHBOARDS);
    }

    if (!selectedUser) {
      navigate(AppRoute.USERS);
    }

    refreshUser();
  }, []);

  const onDeltaUpdate = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (selectedUser) {
        updateUser({
          ...selectedUser,
          delta_dashboard_count: deltas.dashboard,
          delta_website_count: deltas.website
        }).then(() => {
          refreshUser();
          notify('User deltas updated', SnackbarType.SUCCESS);
        }, errorHandler);
      }
    },
    [selectedUser, deltas]
  );

  const onDeactivate = useCallback(() => {
    if (selectedUser) {
      updateUser({ ...selectedUser, active: !selectedUser.active }).then(() => {
        refreshUser();
        setShowActivityPopup(false);
        notify('User status updated', SnackbarType.SUCCESS);
      }, errorHandler);
    }
  }, [selectedUser]);

  const changeState = useCallback(() => {
    if (selectedUser.active) {
      setShowActivityPopup(true);
    } else {
      onDeactivate();
    }
  }, [selectedUser]);

  return (
    <Frame>
      <Panel>
        <h1>Update {selectedUser.name ?? 'user'}</h1>
        <p>You can change the limits of the user's plan and deactivate the user.</p>

        <h2>Plan limits</h2>
        <p>
          Change the default plan limits for the user by setting DELTA values below. Positive values
          increase the limit, and negative values decrease the limit.
        </p>

        <form style={{ marginBottom: '2rem' }} onSubmit={onDeltaUpdate}>
          <Input
            id="delta_dashboard_count"
            value={deltas.dashboard}
            label="Dashboard limit DELTA"
            type={InputType.NUMBER}
            setValue={(val) => setDeltas((old) => ({ ...old, dashboard: Number(val) }))}
          />
          <Input
            id="delta_website_count"
            value={deltas.website}
            label="Website limit DELTA"
            type={InputType.NUMBER}
            setValue={(val) => setDeltas((old) => ({ ...old, website: Number(val) }))}
          />
          <Button isSubmit type={ButtonType.PRIMARY}>
            SAVE DELTA VALUES
          </Button>
        </form>

        <h2>Account status</h2>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
          <p>{selectedUser.active ? 'ACTIVE' : 'INACTIVE'}</p>
          <Button type={ButtonType.PRIMARY} action={changeState}>
            {selectedUser.active ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
        <NavLink to={AppRoute.USERS}>
          <Button type={ButtonType.GHOST}>Back</Button>
        </NavLink>
        {!!showActivityPopup && (
          <DeactivatePopup
            name={selectedUser.name}
            onDelete={onDeactivate}
            onCancel={() => setShowActivityPopup(false)}
          />
        )}
      </Panel>
    </Frame>
  );
};

export default EditUser;
